import { useState } from "react";
import { Link } from "react-router-dom";
import { publico } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ShieldCheck, Lock, ArrowLeft, Sparkles, CheckCircle2, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CanalDenuncia() {
  const [form, setForm] = useState({
    anonimo: true, denunciante_nome: "", denunciante_contato: "",
    categoria: "assedio_moral", descricao: "", envolvidos: "", local: "", data_fato: "",
  });
  const [protocolo, setProtocolo] = useState(null);
  const [loading, setLoading] = useState(false);

  const enviar = async () => {
    if (!form.descricao || form.descricao.length < 30) { toast.error("Descreva com pelo menos 30 caracteres"); return; }
    setLoading(true);
    try {
      const { data } = await publico.denuncia(form);
      setProtocolo(data.protocolo);
    } catch { toast.error("Erro ao registrar. Tente novamente."); } finally { setLoading(false); }
  };

  const copyProto = () => {
    navigator.clipboard.writeText(protocolo);
    toast.success("Protocolo copiado");
  };

  if (protocolo) {
    return (
      <div className="min-h-screen bg-[#0A0B1A] flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[120px]"/>
        </div>
        <div className="relative max-w-lg w-full soeasy-card p-10 text-center fade-up">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 size={32} className="text-emerald-400"/>
          </div>
          <div className="text-emerald-400 text-[10px] font-bold tracking-[0.3em] uppercase mb-3">Denúncia registrada</div>
          <h1 className="font-heading text-3xl text-white mb-2">Recebida com sigilo total</h1>
          <p className="text-[#A1A4C7] mb-6 text-sm">Sua denúncia foi registrada. Guarde o protocolo abaixo — é a única forma de acompanhar o andamento.</p>

          <div className="bg-[#0A0B1A] border border-[#B28F4B]/30 rounded-xl p-5 mb-6">
            <div className="text-[10px] font-bold tracking-widest uppercase text-[#B28F4B] mb-2">Seu protocolo</div>
            <div className="flex items-center justify-center gap-3">
              <div className="font-mono text-2xl text-white font-bold tracking-wider">{protocolo}</div>
              <button onClick={copyProto} data-testid="btn-copy-protocolo" className="text-[#B28F4B] hover:text-[#D4AF37] p-2"><Copy size={16}/></button>
            </div>
          </div>

          <Link to="/login" className="text-[#B28F4B] hover:text-[#D4AF37] text-sm font-semibold">
            Voltar ao login →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0B1A] relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#B28F4B]/10 blur-[120px]"/>
        <div className="absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[#1A1D42] blur-[100px]"/>
      </div>

      <div className="relative max-w-3xl mx-auto p-6 md:p-10 fade-up">
        <div className="flex items-center justify-between mb-10">
          <Logo/>
          <Link to="/login" className="text-[#B28F4B] hover:text-[#D4AF37] text-sm font-semibold flex items-center gap-2">
            <ArrowLeft size={14}/> Ir ao login
          </Link>
        </div>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#B28F4B]/10 border border-[#B28F4B]/30 rounded-full px-4 py-1.5 mb-6">
            <Lock size={13} className="text-[#B28F4B]"/>
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#B28F4B]">Canal Seguro · Sigiloso · Sem login</span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-light text-white leading-tight mb-4">
            Denuncie com<br/>
            <span className="text-[#B28F4B] italic font-serif">total anonimato.</span>
          </h1>
          <p className="text-[#A1A4C7] max-w-xl mx-auto leading-relaxed">
            Canal oficial SOEASY para relatos de conduta inadequada, assédio, fraude ou não conformidade.
            Todas as denúncias são investigadas pela equipe de Compliance.
          </p>
        </div>

        <div className="soeasy-card p-6 md:p-8 space-y-5">
          {/* Toggle anônimo */}
          <div className="bg-[#0A0B1A] border border-[#B28F4B]/20 rounded-xl p-4 flex items-center justify-between">
            <div>
              <div className="text-white font-semibold text-sm">Denúncia anônima</div>
              <div className="text-[#A1A4C7] text-xs mt-0.5">Recomendado. Não solicitamos seus dados.</div>
            </div>
            <button
              onClick={() => setForm({ ...form, anonimo: !form.anonimo })}
              data-testid="toggle-anonimo"
              className={`w-12 h-7 rounded-full transition-colors relative ${form.anonimo ? "bg-[#B28F4B]" : "bg-[#131532]"}`}
            >
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${form.anonimo ? "translate-x-6" : "translate-x-1"}`}/>
            </button>
          </div>

          {!form.anonimo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Seu nome (opcional)"><input value={form.denunciante_nome} onChange={e => setForm({ ...form, denunciante_nome: e.target.value })} className="input-pub"/></Field>
              <Field label="Contato (e-mail ou WhatsApp)"><input value={form.denunciante_contato} onChange={e => setForm({ ...form, denunciante_contato: e.target.value })} className="input-pub"/></Field>
            </div>
          )}

          <Field label="Categoria">
            <select value={form.categoria} onChange={e => setForm({ ...form, categoria: e.target.value })} className="input-pub" data-testid="select-categoria">
              <option value="assedio_moral">Assédio Moral</option>
              <option value="assedio_sexual">Assédio Sexual</option>
              <option value="discriminacao">Discriminação</option>
              <option value="fraude">Fraude</option>
              <option value="corrupcao">Corrupção / Suborno</option>
              <option value="conflito_interesse">Conflito de Interesse</option>
              <option value="vazamento_dados">Vazamento de Dados</option>
              <option value="conduta_inadequada">Conduta Inadequada</option>
              <option value="outro">Outro</option>
            </select>
          </Field>

          <Field label="Descrição do fato">
            <textarea
              value={form.descricao}
              onChange={e => setForm({ ...form, descricao: e.target.value })}
              rows={6}
              placeholder="Descreva o que aconteceu, quando, onde, quem estava envolvido. Quanto mais detalhes, melhor a investigação."
              className="input-pub resize-none"
              data-testid="textarea-descricao"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Local"><input value={form.local} onChange={e => setForm({ ...form, local: e.target.value })} className="input-pub" placeholder="Onde ocorreu"/></Field>
            <Field label="Data do fato"><input type="date" value={form.data_fato} onChange={e => setForm({ ...form, data_fato: e.target.value })} className="input-pub"/></Field>
          </div>

          <Field label="Pessoas envolvidas (opcional)">
            <input value={form.envolvidos} onChange={e => setForm({ ...form, envolvidos: e.target.value })} className="input-pub" placeholder="Nomes ou iniciais dos envolvidos"/>
          </Field>

          <div className="bg-[#B28F4B]/8 border border-[#B28F4B]/25 rounded-xl p-4 flex gap-3">
            <ShieldCheck size={20} className="text-[#B28F4B] flex-shrink-0 mt-0.5"/>
            <div className="text-xs text-[#A1A4C7] leading-relaxed">
              <strong className="text-[#B28F4B]">Compromisso de sigilo:</strong> Sua identidade (se informada) e todos os dados são criptografados.
              Apenas a equipe de Compliance tem acesso. Nenhuma retaliação é permitida.
            </div>
          </div>

          <button
            onClick={enviar}
            disabled={loading}
            data-testid="btn-enviar-denuncia"
            className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>}
            Registrar Denúncia com Sigilo
          </button>
        </div>
      </div>

      <style>{`.input-pub { width: 100%; background: #0A0B1A; border: 1px solid rgba(178,143,75,0.2); color: #fff; padding: 12px 14px; border-radius: 8px; outline: none; font-size: 14px; font-family: inherit; transition: border-color 0.2s; } .input-pub:focus { border-color: #B28F4B; box-shadow: 0 0 0 3px rgba(178,143,75,0.15); }`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">{label}</label>
      {children}
    </div>
  );
}
